import React from "react";
import { FiX, FiMail, FiPhone, FiUser, FiCalendar, FiRefreshCw, FiMapPin, FiTruck, FiUsers } from "react-icons/fi";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import "./CustomerDetailDrawer.css";

const fmt = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const fmtCurrency = (val, symbol = "€") =>
  val != null ? `${symbol}${Number(val).toLocaleString()}` : "—";

const initials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

const COUNTRY_COLORS = {
  Spain:   { bg: "#E6F1FB", text: "#0C447C" },
  Latvia:  { bg: "#EAF3DE", text: "#3B6D11" },
  Estonia: { bg: "#FAEEDA", text: "#633806" },
  Czechia: { bg: "#EEEDFE", text: "#3C3489" },
  default: { bg: "#F1EFE8", text: "#444441" },
};

const CountryBadge = ({ country }) => {
  const c = COUNTRY_COLORS[country] || COUNTRY_COLORS.default;
  return (
    <span
      className="cdd-country-badge"
      style={{ background: c.bg, color: c.text }}
    >
      {country}
    </span>
  );
};

const FieldRow = ({ icon: Icon, label, value, isLink }) => (
  <div className="cdd-field-row">
    <span className="cdd-field-label">
      {Icon && <Icon className="cdd-field-icon" />}
      {label}
    </span>
    <span className={`cdd-field-value${isLink ? " cdd-link" : ""}`}>
      {value || "—"}
    </span>
  </div>
);

const MetricCard = ({ label, value }) => (
  <div className="cdd-metric-card">
    <div className="cdd-metric-label">{label}</div>
    <div className="cdd-metric-value">{value}</div>
  </div>
);

const AddressCard = ({ address }) => {
  const lines = [
    address.street,
    address.landmark,
    address.city || address.state,
    address.postalCode,
  ].filter(Boolean);

  return (
    <div className="cdd-address-card">
      <CountryBadge country={address.country} />
      <div className="cdd-address-name">{address.line1}</div>
      {lines.map((l, i) => (
        <div key={i} className="cdd-address-sub">{l}</div>
      ))}
      {address.deliveryName && (
        <div className="cdd-address-sub">{address.deliveryName}</div>
      )}
    </div>
  );
};

const CustomerDetailDrawer = ({ data, loading, onClose }) => {
  return (
    <>
      <div className="cdd-overlay" onClick={onClose} />
      <div className="cdd-drawer">
        {/* Header */}
        {/* <div className="cdd-drawer-header">
          <h2 className="cdd-drawer-title">Customer Details</h2>
          
          <button className="cdd-close-btn" onClick={onClose} aria-label="Close">
            <FiX size={20} />
          </button>
        </div> */}
 <button className="cdd-close-btn" onClick={onClose} aria-label="Close">
            <FiX size={20} />
          </button>
        {/* Body */}
        <div className="cdd-drawer-body">
          {loading ? (
            <div className="cdd-loading">
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 34, color: "#f50087" }}
                    spin
                  />
                }
              />
              <span>Loading</span>
            </div>
          ) : !data ? (
            <div className="cdd-empty">Failed to load customer details.</div>
          ) : (
            <>
              {/* Identity */}
              <div className="cdd-section">
                <div className="cdd-profile-header">
                  <div className="cdd-avatar">{initials(data.name)}</div>
                  <div>
                    <div className="cdd-profile-name">{data.name}</div>
                    <div className="cdd-profile-badges">
                      <span className="cdd-badge cdd-badge-info">
                        {data.customerCode}
                      </span>
                      <span className="cdd-badge cdd-badge-success">
                        {data.entity}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Metrics */}
                <div className="cdd-metric-grid">
                  <MetricCard
                    label="Credit limit"
                    value={fmtCurrency(data.creditLimit, data.currency)}
                  />
                  <MetricCard
                    label="Currency"
                    value={`${data.currency} EUR`}
                  />
                  <MetricCard
                    label="Payment terms"
                    value={data.paymentTerm || "—"}
                  />
                  <MetricCard
                    label="Sales group"
                    value={data.salesOrderGroup || "—"}
                  />
                </div>

                {/* Fields */}
                <FieldRow icon={FiMail}      label="Email"        value={data.email}           isLink />
                <FieldRow icon={FiPhone}     label="Phone"        value={data.phoneNo === "-" ? null : data.phoneNo} />
                <FieldRow icon={FiUser}      label="Salesperson"  value={data.salesPersonName} />
                {/* <FieldRow icon={FiCalendar}  label="Created"      value={fmt(data.createdAt)}  /> */}
                {/* <FieldRow icon={FiRefreshCw} label="Last updated" value={fmt(data.updatedAt)}  /> */}
              </div>

              {/* Billing Address */}
              {data.billingAddress && (
                <div className="cdd-section">
                  <div className="cdd-section-title">
                    <FiMapPin className="cdd-section-icon" />
                    Billing address
                  </div>
                  <div className="cdd-billing-body">
                    <div>{data.billingAddress.line1}</div>
                    <div className="cdd-muted">{data.billingAddress.street}</div>
                    {data.billingAddress.landmark && (
                      <div className="cdd-muted">{data.billingAddress.landmark}</div>
                    )}
                    <div className="cdd-muted">
                      {[data.billingAddress.city, data.billingAddress.postalCode]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                    <div className="cdd-muted">{data.billingAddress.country}</div>
                  </div>
                </div>
              )}

              {/* Delivery Addresses */}
              {data.addresses?.length > 0 && (
                <div className="cdd-section">
                  <div className="cdd-section-title">
                    <FiTruck className="cdd-section-icon" />
                    Delivery addresses
                    <span className="cdd-section-count">
                      {data.addresses.length} location{data.addresses.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="cdd-address-grid">
                    {data.addresses.map((a) => (
                      <AddressCard key={a.id} address={a} />
                    ))}
                  </div>
                </div>
              )}

              {/* Delivery Contacts */}
              {data.deliveryContacts?.length > 0 && (
                <div className="cdd-section">
                  <div className="cdd-section-title">
                    <FiUsers className="cdd-section-icon" />
                    Delivery contacts
                    <span className="cdd-section-count">
                      {data.deliveryContacts.length} contact{data.deliveryContacts.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="cdd-contacts-list">
                    {data.deliveryContacts.map((c) => (
                      <div key={c.id} className="cdd-contact-card">
                        <div className="cdd-contact-avatar">
                          {initials(c.name)}
                        </div>
                        <div>
                          <div className="cdd-contact-name">{c.name}</div>
                          <div className="cdd-contact-email">{c.email}</div>
                          {c.phoneNo && (
                            <div className="cdd-contact-phone">{c.phoneNo}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CustomerDetailDrawer;